import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { debounce } from 'lodash';

import { getAccountLocationsByConfirmationToken } from '../../api/services/Organizers';
import * as Constants from '../../constants';

export const useSignupLocationOptions = (confirmationToken: string) => {
  const [signupLocationOptions, setSignupLocationOptions] = useState<
    Pick<AccountLocation, 'id' | 'name' | 'address'>[]
  >([]);
  const [isLoading, setIsLoading] = useState(true);
  const abortController = useRef<AbortController>();
  const [queryParams, setQueryParams] = useState<{ perPage: number; page: number; q: string | null }>({
    page: 1,
    perPage: 10,
    q: null,
  });
  const debounceHandleQuery = useMemo(() => debounce(handleQuery, Constants.DEBOUNCE_TIME), []);

  function handleQuery(query) {
    if (!query) {
      setIsLoading(true);
      setSignupLocationOptions([]);
      setQueryParams((prevQueryParams) => ({
        q: prevQueryParams.q,
        page: 1,
        perPage: prevQueryParams.perPage,
      }));
    }

    if (query !== queryParams.q && query.length >= Constants.MINIMUM_SEARCH_STRING_LENGTH) {
      setIsLoading(true);
      setSignupLocationOptions([]);
      setQueryParams((prevQueryParams) => ({
        q: query,
        page: 1,
        perPage: prevQueryParams.perPage,
      }));
    }
  }

  const handlePagination = () => {
    setIsLoading(true);
    setQueryParams((prevQueryParams) => ({
      q: prevQueryParams.q,
      page: prevQueryParams.page + 1,
      perPage: queryParams.perPage,
    }));
  };

  const getQueryParams = useCallback(() => {
    const queryParamsToSubmit = {
      page: queryParams.page,
      perPage: queryParams.perPage,
      q: queryParams.q,
      confirmationToken: confirmationToken,
    };

    return queryParamsToSubmit;
  }, [queryParams]);

  const fetchSignupLocations = useCallback(async () => {
    abortController.current?.abort();
    abortController.current = new AbortController();
    const queryParamsUpdated = { ...getQueryParams(), confirmationToken };
    const response = await getAccountLocationsByConfirmationToken(queryParamsUpdated, abortController.current?.signal);

    if (response?.type === 'success') {
      const newLocations = response.data;

      if (newLocations?.length > 0) {
        setIsLoading(newLocations.length === Constants.ITEM_LIMIT_PER_PAGE);
        setSignupLocationOptions((prevLocations) =>
          queryParamsUpdated.page === 1 ? newLocations : prevLocations.concat(newLocations)
        );
      } else if (!abortController.current?.signal.aborted) {
        setIsLoading(false);
      }
    }
    setIsLoading(false);
  }, [getQueryParams]);

  useEffect(() => {
    fetchSignupLocations();
  }, [fetchSignupLocations]);

  useEffect(() => {
    return () => {
      abortController.current?.abort();
    };
  }, []);

  return {
    signupLocationOptions,
    queryParams,
    debounceHandleQuery,
    handlePagination,
    isLoading,
  };
};
